import { Component, OnDestroy, OnInit } from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../services/mdr-dashboards.service';
import { ApexChart, ApexDataLabels, ApexLegend, ApexNonAxisChartSeries, ApexPlotOptions, ApexResponsive, ApexStroke } from 'ng-apexcharts';
import { MdrServiceWidgets } from '../../interfaces/mdr-dashboards-widgets.interface';
import { distinctUntilChanged } from 'rxjs/operators';
import { TimeframeData } from '../../../timeframe/interfaces/TimeframeData';
import { TimeframeService } from '../../../timeframe/services/timeframe.service';
import { OrganizationData } from '../../../organizations/interfaces/OrganizationData';
import { isEqual } from 'lodash-es';
import { Subscription } from 'rxjs';
import { ThemeService } from '../../../../services/theme.service';

export type ChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    dataLabels: ApexDataLabels;
    labels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    legend: ApexLegend;
    stroke: ApexStroke;
};

@Component({
    selector: 'app-mdr-dashboards-activity',
    templateUrl: './mdr-dashboards-mdr-service.component.html',
    styleUrls: ['./mdr-dashboards-mdr-service.component.scss'],
    standalone: false,
})
export class MdrDashboardsMdrServiceComponent implements OnInit, OnDestroy {
    public orgSubscription: Subscription;
    public timeframeSubscription: Subscription;
    public dataSource: MdrServiceWidgets;

    public constructor(
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
        public timeframeService: TimeframeService,
        public themeService: ThemeService,
    ) {}

    ngOnDestroy(): void {
        this.orgSubscription?.unsubscribe();
        this.timeframeSubscription?.unsubscribe();
    }

    public ngOnInit() {
        this.timeframeSubscription = this.timeframeService
            .selectedTimeframe$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((timeframe: TimeframeData) => {
                this.updateDataSource(timeframe, this.organizationService.currentlySelectedOrganization());
            });
        this.orgSubscription = this.organizationService
            .selectedOrganization$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((organization: OrganizationData) => {
                this.updateDataSource(this.timeframeService.currentlySelectedTimeframe(), organization);
                this.mdrDashboardsService.performDetectChanges.next(true);
            });
    }

    /**
     * Use this function to update each widget when one of the selectors (organization and timeframe) are changed
     * @param timeframe - retrieve data for the selected timeframe
     * @param organization - retrieve data for the selected organization
     */
    public updateDataSource(timeframe: TimeframeData | null, organization: OrganizationData | null) {
        this.dataSource = {
            indicatorOptions: [
                {
                    title: 'Active investigations',
                    value: 78,
                    delta: '',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/active-investigations', organization),
                    showCheckMarkOnEmpty: true,
                    titleForEmpty: 'No active investigation',
                    id: 'active_investigations_id',
                },
                {
                    title: 'Pending response',
                    value: 23,
                    delta: '',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/pending-response', organization),
                    showCheckMarkOnEmpty: true,
                    titleForEmpty: 'None pending response',
                    id: 'pending_response_indicator',
                },
                {
                    title: 'Mean time to acknowledge',
                    value: 12,
                    delta: 'minutes',
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/mean-time-ack', organization),
                    id: 'mean_time_to_acknowledge_indicator',
                },
            ],
            investigationSeverityTrend: {
                noDataLegend: [
                    {
                        name: 'Critical',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'High',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'Medium',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'Low',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                    {
                        name: 'No threat',
                        value: '- -',
                        color: this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D',
                    },
                ],
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/investigation-severity-trend', organization),
                strokeColors: ['#A2D179', '#A2D179', '#FFD23D', '#EF6E45', '#E90A0A'],
                colors: ['#DAEDC9', '#FFEF99', '#FFCFB2', '#F69D9D', '#DA6268'],
                title: 'Investigation severity trend',
                columns: [],
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/investigations/browse',
                    drilldownTitle: false,
                    drilldownFilters: [
                        { item: 'timeframe' },
                        {
                            item: {
                                apiName: 'name',
                                nativeName: 'severity',
                            },
                        },
                    ],
                },
                approximate: true,
            },
            huntOutcomes: {
                noDataSeries: [
                    {
                        name: 'All clear',
                        data: [10, 10],
                    },
                    {
                        name: 'Recommendations',
                        data: [10, 10],
                    },
                    {
                        name: 'Actions',
                        data: [10, 10],
                    },
                    {
                        name: 'Threat model update',
                        data: [10, 10],
                    },
                ],
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/hunt-outcomes', organization),
                colors: ['#1B8AA8', '#67B7DC', '#A367DC', '#295CA5'],
                categories: ['Targeted', 'Risk-based'],
                title: 'Threat Hunt Outcomes',
                approximate: true,
            },
            investigationResolution: {
                id: 'top_investigations_categories_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-investigation-categories', organization),
                no_rows: 10,
                columns: ['category', 'value'],
                columnsWidth: [
                    {
                        column: 'category',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top investigation categories',
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/investigations/browse',
                    drilldownTitle: true,
                    drilldownFilters: [{ item: 'timeframe' }, { item: 'category' }],
                    drilldownTitleFilters: [{ item: 'timeframe' }],
                },
            },
            topInvestigationsHosts: {
                id: 'top_investigations_hosts_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-investigation-hosts', organization),
                no_rows: 10,
                columns: ['logo', 'name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top investigation hosts',
            },
            topInvestigationsSignature: {
                id: 'top_investigations_siganture_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-investigation-signature-names', organization),
                no_rows: 10,
                columns: ['name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top investigation signature names',
            },
            recentInvestigations: {
                id: 'recent_investigations_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/recent-investigations', organization),
                no_rows: 5,
                columns: ['date', 'category', 'description', 'recommendations'],
                columnsWidth: [
                    {
                        column: 'date',
                        width: '15%',
                    },
                    {
                        column: 'category',
                        width: '25%',
                    },
                    {
                        column: 'description',
                        width: '45%',
                    },
                    {
                        column: 'recommendations',
                        width: '15%',
                    },
                ],
                title: 'Recent investigations',
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/investigations/browse',
                    drilldownFilters: [
                        { item: 'id' },
                        { item: 'cid' },
                        {
                            item: {
                                apiName: 'cname',
                                nativeName: 'company_name',
                            },
                        },
                    ],
                    drilldownTitle: true,
                    drilldownTitleFilters: [{ item: 'timeframe' }],
                },
            },
            recentHunts: {
                id: 'recent_hunts_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/recent-hunts', organization),
                no_rows: 5,
                columns: ['date', 'category', 'description', 'result', 'recommendations'],
                columnsWidth: [
                    {
                        column: 'date',
                        width: '10%',
                    },
                    {
                        column: 'category',
                        width: '10%',
                    },
                    {
                        column: 'description',
                        width: '30%',
                    },
                    {
                        column: 'result',
                        width: '30%',
                    },
                    {
                        column: 'recommendations',
                        width: '20%',
                    },
                ],
                title: 'Recent Threat Hunts',
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/threat-hunts/browse',
                    drilldownFilters: [
                        { item: 'id' },
                        { item: 'cid' },
                        {
                            item: {
                                apiName: 'cname',
                                nativeName: 'company_name',
                            },
                        },
                    ],
                    drilldownTitle: true,
                    drilldownTitleFilters: [{ item: 'timeframe' }],
                },
            },
        };
    }
}
